import { useMemo } from "react";
import { useStore } from "zustand";
import { coreStore } from "@/lib/stores/core-store";

export function usePresence(firebaseUid: string) {
	return useStore(coreStore, (state) => {
		const presence = state.users.get(firebaseUid)?.presence;
		return presence?.status || "offline";
	});
}

export function usePresenceLabel(firebaseUid: string) {
	return useStore(coreStore, (state) => {
		const presence = state.users.get(firebaseUid)?.presence;
		if (!presence || presence.status === "offline") return "Offline";
		if (presence.activityStatus === "idle") return "Idle";
		return "Online";
	});
}

export function usePresenceWithChannel(firebaseUid: string) {
	return useStore(coreStore, (state) => {
		const presence = state.users.get(firebaseUid)?.presence;
		return {
			status: presence?.status || "offline",
			currentChannelId: presence?.currentChannelId ?? null,
		};
	});
}

export function useUserCurrentChannel(firebaseUid: string) {
	return useStore(coreStore, (state) => {
		const presence = state.users.get(firebaseUid)?.presence;
		return presence?.currentChannelId ?? null;
	});
}

export function useChannelParticipants(channelId: string | null) {
	const users = useStore(coreStore, (state) => state.users);

	return useMemo(() => {
		if (!channelId) return [];

		const participants: Array<{
			firebaseUid: string;
			user: ReturnType<typeof users.get> | undefined;
			isMicrophoneEnabled: boolean;
			isCameraEnabled: boolean;
		}> = [];

		for (const [uid, entry] of users) {
			if (entry.presence?.currentChannelId === channelId) {
				participants.push({
					firebaseUid: uid,
					user: entry,
					isMicrophoneEnabled: entry.presence?.isMicrophoneEnabled ?? false,
					isCameraEnabled: entry.presence?.isCameraEnabled ?? false,
				});
			}
		}

		return participants;
	}, [channelId, users]);
}

export function useCategoryVideoParticipants(channelIds: string[]) {
	const users = useStore(coreStore, (state) => state.users);

	return useMemo(() => {
		if (channelIds.length === 0) return [];

		const channelIdSet = new Set(channelIds);
		const participants: Array<{
			firebaseUid: string;
			user: ReturnType<typeof users.get> | undefined;
			channelId: string;
			isMicrophoneEnabled: boolean;
			isCameraEnabled: boolean;
		}> = [];

		for (const [uid, entry] of users) {
			const currentChannelId = entry.presence?.currentChannelId;
			if (currentChannelId && channelIdSet.has(currentChannelId)) {
				participants.push({
					firebaseUid: uid,
					user: entry,
					channelId: currentChannelId,
					isMicrophoneEnabled: entry.presence?.isMicrophoneEnabled ?? false,
					isCameraEnabled: entry.presence?.isCameraEnabled ?? false,
				});
			}
		}

		return participants;
	}, [channelIds, users]);
}
